<template>
  <div class="lesson-preview-container">
    <!-- Loading Overlay -->
    <LoadingOverlay
      :model-value="isLoading"
      message="Loading Lesson Materials..."
      :full-screen="true"
      :teleport-to-body="true"
    />

    <!-- Error State -->
    <div v-if="!isLoading && error" class="error-state">
      <div class="error-content">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <h2>Failed to Load Lesson Materials</h2>
        <p>{{ error }}</p>
        <button class="back-button" @click="goBack">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Editor
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div
      v-if="!isLoading && !error && activityData.title"
      class="lesson-content-wrapper"
    >
      <div class="lesson-main-content">
        <div class="lesson-header">
          <div class="lesson-header-content">
            <div class="lesson-navigation-container">
              <ul class="lesson-navigation-list">
                <li>
                  <a @click="goBack" style="cursor: pointer">
                    Lesson Materials Editor
                  </a>
                </li>
                <li>/</li>
                <li class="lesson-navigation-title">
                  {{ activityData.title }}
                </li>
              </ul>
            </div>
            <div class="lesson-title-container">
              <h1 class="lesson-title">
                {{ activityData.title || "Untitled Lesson Materials" }}
              </h1>
              <div
                v-if="activityData.short_description"
                class="lesson-subtitle"
              >
                {{ activityData.short_description }}
              </div>
              <div class="lesson-meta">
                <div class="lesson-meta-item">
                  <div class="lesson-meta-item-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                  </div>
                  <span>{{ activityData.duration || "0" }} minutes</span>
                </div>
                <div v-if="activityData.grade" class="lesson-meta-item">
                  <div class="lesson-meta-item-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                      <path d="M6 12v5c3 3 9 3 12 0v-5" />
                    </svg>
                  </div>
                  <span>{{ activityData.grade }}</span>
                </div>
                <div v-if="activityData.subject" class="lesson-meta-item">
                  <div class="lesson-meta-item-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path
                        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                      />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>
                  </div>
                  <span style="text-transform: capitalize">{{
                    activityData.subject
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="lesson-main-container">
          <div class="lesson-content" :class="{ expanded: sidebarCollapsed }">
            <div class="lesson-tabs">
              <div
                v-for="(tab, index) in activityData.tabs"
                :key="index"
                class="lesson-tab"
                :class="{ active: activeTabIndex === index }"
                @click="activeTabIndex = index"
              >
                {{ tab.name }}
                <div
                  v-if="tab.duration && activeTabIndex === index"
                  class="tab-duration"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <span>{{ tab.duration }} Minutes</span>
                </div>
              </div>
            </div>

            <div
              class="lesson-tab-content"
              ref="lessonTabContentRef"
              v-if="activityData.tabs && activityData.tabs.length > 0"
            >
              <template v-if="currentTab.type === 'assessment'">
                <AssessmentView :assessments="currentTab.assessments" />
              </template>
              <template v-else>
                <div
                  v-for="(section, sectionIndex) in getCurrentTabSections()"
                  :key="sectionIndex"
                  class="lesson-section"
                >
                  <div class="tiptap-content" v-html="section.content"></div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Resources Section -->
        <div
          v-if="hasResources && activeTabIndex === 0"
          class="resources-section"
        >
          <h2>Resources</h2>
          <div class="resources-buttons">
            <!-- Slide Deck Button -->
            <v-btn
              v-if="activityData.slide_url"
              class="custom-button"
              color="#1E3A5F"
              size="large"
              style="
                border-radius: 15px;
                text-transform: none;
                font-size: var(--fs-big);
              "
              @click="openCopySlideModal"
            >
              <v-icon start>mdi-content-copy</v-icon>
              <span>Copy <strong>Slide Deck</strong> to your Google Drive</span>
            </v-btn>

            <!-- Instructional Materials -->
            <v-btn
              v-for="material in activityData.educator_guide_instructional"
              :key="material.id || material.name"
              class="custom-button"
              color="#1E3A5F"
              size="large"
              style="
                border-radius: 15px;
                text-transform: none;
                font-size: var(--fs-big);
              "
              @click="openCopyResourceModal(material)"
            >
              <v-icon start>mdi-content-copy</v-icon>
              <span
                >Copy <strong>{{ material.name }}</strong> to your Google
                Drive</span
              >
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Right Sidebar -->
      <div class="activity-sidebar" :class="{ collapsed: sidebarCollapsed }">
        <div class="sidebar-header">
          <button
            class="sidebar-toggle"
            @click="sidebarCollapsed = !sidebarCollapsed"
            :title="sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline
                :points="
                  sidebarCollapsed ? '9 18 15 12 9 6' : '15 18 9 12 15 6'
                "
              ></polyline>
            </svg>
          </button>
        </div>

        <div class="sidebar-content" v-if="!sidebarCollapsed">
          <!-- Maker Approach Section -->
          <div
            v-if="activityData.maker_approach"
            class="sidebar-section maker-approach-section"
          >
            <h3 class="sidebar-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 20h9" />
                <path
                  d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"
                />
              </svg>
              Maker Approach
            </h3>
            <div class="maker-approach-content">
              <v-tooltip
                location="bottom"
                :text="makerApproach?.description"
                :disabled="!makerApproach"
                content-class="custom-tooltip"
                transition="fade-transition"
                open-delay="200"
                close-delay="100"
                max-width="400"
              >
                <template v-slot:activator="{ props }">
                  <p class="maker-approach-text tooltip-trigger" v-bind="props">
                    {{ makerApproach?.title || activityData.maker_approach }}
                    <svg
                      v-if="makerApproach"
                      xmlns="http://www.w3.org/2000/svg"
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="info-icon"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4" />
                      <path d="M12 8h.01" />
                    </svg>
                  </p>
                </template>
              </v-tooltip>
            </div>
          </div>

          <!-- Materials Provided Section -->
          <div
            v-if="
              activityData.material_provided &&
              activityData.material_provided.length > 0
            "
            class="sidebar-section materials-section"
          >
            <h3 class="sidebar-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"
                />
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
              </svg>
              Materials
              <span class="materials-count"
                >({{ activityData.material_provided.length }})</span
              >
            </h3>
            <div class="materials-list">
              <div
                v-for="material in activityData.material_provided"
                :key="material.id"
                class="material-item"
              >
                <div class="material-header">
                  <h4 class="material-name">{{ material.name }}</h4>
                  <span v-if="material.includedInMC" class="included-badge">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    MakerCart
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Standards Section -->
          <div
            v-if="standardCategories.length > 0"
            class="sidebar-section standards-section"
          >
            <h3 class="sidebar-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                <path d="M6 12v5c3 3 9 3 12 0v-5" />
              </svg>
              Standards
            </h3>
            <div class="standards-list">
              <div
                v-for="(category, index) in standardCategories"
                :key="index"
                class="standard-category"
              >
                <div class="standard-category-title">{{ category.title }}</div>
                <div class="standard-terms">
                  <template v-if="category.terms && category.terms.length > 0">
                    <template
                      v-for="(term, termIndex) in category.terms"
                      :key="termIndex"
                    >
                      <template v-if="term.description">
                        <v-tooltip
                          :text="term.description"
                          location="top"
                          max-width="300"
                          content-class="custom-tooltip"
                        >
                          <template v-slot:activator="{ props }">
                            <button class="standard-tag" v-bind="props">
                              {{ term.title }}
                            </button>
                          </template>
                        </v-tooltip>
                      </template>
                      <template v-else>
                        <button class="standard-tag">{{ term.title }}</button>
                      </template>
                    </template>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Activity Info Section -->
          <div class="sidebar-section activity-info-section">
            <h3 class="sidebar-section-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              Activity Details
            </h3>
            <div class="activity-details">
              <div class="detail-item" v-if="activityData.grade">
                <span class="detail-label">Grade Level:</span>
                <span class="detail-value">{{ activityData.grade }}</span>
              </div>
              <div class="detail-item" v-if="activityData.subject">
                <span class="detail-label">Subject:</span>
                <span class="detail-value">{{ activityData.subject }}</span>
              </div>
              <div class="detail-item" v-if="activityData.focus">
                <span class="detail-label">Focus:</span>
                <span class="detail-value" style="text-transform: capitalize">{{
                  activityData.focus
                }}</span>
              </div>
              <div class="detail-item" v-if="activityData.duration">
                <span class="detail-label">Duration:</span>
                <span class="detail-value"
                  >{{ activityData.duration }} minutes</span
                >
              </div>
              <div class="detail-item" v-if="activityData.status">
                <span class="detail-label">Status:</span>
                <span
                  class="detail-value status"
                  :class="activityData.status.toLowerCase()"
                >
                  {{ activityData.status }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div v-if="!isLoading && !error" class="action-buttons">
      <button class="back-button" @click="goBack">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12 19 5 12 12 5"></polyline>
        </svg>
        Back to Editor
      </button>
    </div>

    <!-- Copy Slide Deck Modal -->
    <CopySlideDeck
      :is-open="isCopySlideModalOpen"
      :file-url="currentCopyResource.url"
      :file-name="currentCopyResource.name"
      @update:is-open="isCopySlideModalOpen = $event"
    />
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  computed,
  nextTick,
  watch,
  onBeforeUnmount,
  defineProps,
} from "vue";
import { useRouter, useRoute } from "vue-router";
import { setupSlideThumbnailHandlers } from "@/tiptap-extensions/slide-deck/slideModalHelper";
import AssessmentView from "@/components/lesson-library/AssessmentView.vue";
import CopySlideDeck from "@/components/lesson-library/CopySlideDeck.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import makercartActivityService from "@/services/makercartActivityService";
import cookiesService from "@/services/cookiesService";
import { MAKER_APPROACH_DETAIL, CATEGORY_TYPES } from "@/utils/constants.js";

defineProps({
  id: {
    type: String,
    default: null,
  },
});

const router = useRouter();
const route = useRoute();
const activeTabIndex = ref(0);
const sidebarCollapsed = ref(false);
const isAuthenticated = ref(false);
const lessonTabContentRef = ref(null);
const isCopySlideModalOpen = ref(false);
const currentCopyResource = ref({ url: "", name: "" });
const isLoading = ref(true);
const error = ref("");

// Default activity data structure
const defaultActivityShell = {
  id: null,
  title: null,
  short_description: null,
  duration: null,
  grade: null,
  subject: null,
  focus: null,
  maker_approach: null,
  slide_url: "",
  educator_guide_instructional: [],
  material_provided: [],
  categories: [],
  tabs: [],
  status: "",
  public_visibility: false,
};

const activityData = ref(JSON.parse(JSON.stringify(defaultActivityShell)));

// Move function declarations to the top level
function initAccordions(container) {
  if (!container) return;
  const tooltipBlocks = container.querySelectorAll(".tooltip-block");
  tooltipBlocks.forEach((block) => {
    block.classList.remove("is-open");
    block.removeAttribute("data-open");
    const titleLine = block.querySelector(".tooltip-title-line");
    const bodyWrapper = block.querySelector(".tooltip-body-wrapper");
    const arrowEl = block.querySelector(".accordion-arrow");
    if (titleLine && bodyWrapper) {
      const titleEl = block.querySelector(".tooltip-title");
      const bodyEl = block.querySelector(".tooltip-body");
      if (titleEl) titleEl.setAttribute("contenteditable", "false");
      if (bodyEl) bodyEl.setAttribute("contenteditable", "false");

      const existingHandler = titleLine.__accordionClickHandler__;
      if (existingHandler) {
        titleLine.removeEventListener("click", existingHandler);
      }

      const toggleAccordion = () => {
        const isOpen = block.classList.contains("is-open");
        if (isOpen) {
          block.classList.remove("is-open");
          bodyWrapper.classList.remove("is-open");
          if (arrowEl) arrowEl.classList.remove("is-open");
          block.removeAttribute("data-open");
        } else {
          block.classList.add("is-open");
          bodyWrapper.classList.add("is-open");
          if (arrowEl) arrowEl.classList.add("is-open");
          block.setAttribute("data-open", "");
        }
      };
      titleLine.addEventListener("click", toggleAccordion);
      titleLine.__accordionClickHandler__ = toggleAccordion;
    }
  });
}

let domObserver = null;

const runDomObserver = () => {
  const contentArea = lessonTabContentRef.value;
  if (contentArea) {
    initAccordions(contentArea);
    setupSlideThumbnailHandlers(contentArea);

    if (typeof MutationObserver !== "undefined") {
      domObserver = new MutationObserver((mutationsList) => {
        let needsAccordionInit = false;
        let needsSlideHandlerInit = false;

        for (const mutation of mutationsList) {
          if (mutation.type === "childList") {
            for (const node of Array.from(mutation.addedNodes).concat(
              Array.from(mutation.removedNodes)
            )) {
              if (node.nodeType === Node.ELEMENT_NODE) {
                if (
                  node.matches(".tooltip-block") ||
                  node.querySelector(".tooltip-block")
                ) {
                  needsAccordionInit = true;
                }
                if (
                  node.matches(".slide-deck-container") ||
                  node.querySelector(".slide-deck-container")
                ) {
                  needsSlideHandlerInit = true;
                }
              }
            }
          }
        }

        if (needsAccordionInit || needsSlideHandlerInit) {
          nextTick(() => {
            if (needsAccordionInit && lessonTabContentRef.value)
              initAccordions(lessonTabContentRef.value);
            if (needsSlideHandlerInit && lessonTabContentRef.value)
              setupSlideThumbnailHandlers(lessonTabContentRef.value);
          });
        }
      });

      domObserver.observe(contentArea, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }
  }
};

const currentTab = computed(() => {
  if (
    !activityData.value.tabs ||
    activeTabIndex.value >= activityData.value.tabs.length
  ) {
    return { type: null, assessments: [], sections: [] };
  }
  return activityData.value.tabs[activeTabIndex.value];
});

const getCurrentTabSections = () => {
  if (
    activityData.value.tabs &&
    activityData.value.tabs.length > 0 &&
    activeTabIndex.value < activityData.value.tabs.length
  ) {
    return activityData.value.tabs[activeTabIndex.value].sections || [];
  }
  return [];
};

const hasResources = computed(() => {
  return (
    activityData.value.slide_url ||
    (activityData.value.educator_guide_instructional &&
      activityData.value.educator_guide_instructional.length > 0)
  );
});

const makerApproach = computed(() => {
  const approach = activityData.value.maker_approach?.toLowerCase();
  return MAKER_APPROACH_DETAIL[approach] || "";
});

// Filter and group standard categories for sidebar display
const standardCategories = computed(() => {
  // First filter standard categories
  const filteredCategories = activityData.value.categories.filter(
    (category) => {
      const isMakerCartCategory = category.category_for.includes(
        CATEGORY_TYPES.LessonMaterials
      );
      const isStandardCategory =
        category.name && category.name.includes("standard");
      return isMakerCartCategory && isStandardCategory;
    }
  );

  // Group categories by name and merge terms
  const groupedCategories = {};

  filteredCategories.forEach((category) => {
    const categoryName = category.name;

    if (!groupedCategories[categoryName]) {
      // Create new grouped category
      groupedCategories[categoryName] = {
        id: category.id,
        name: category.name,
        title: category.title,
        category_for: category.category_for,
        terms: [...(category.terms || [])],
      };
    } else {
      // Merge terms from categories with the same name
      const existingTermIds = new Set(
        groupedCategories[categoryName].terms.map((term) => term.id)
      );

      // Add new terms that don't already exist
      const newTerms = (category.terms || []).filter(
        (term) => !existingTermIds.has(term.id)
      );

      groupedCategories[categoryName].terms.push(...newTerms);
    }
  });

  // Convert back to array
  return Object.values(groupedCategories);
});

const openCopySlideModal = () => {
  currentCopyResource.value = {
    url: activityData.value.slide_url,
    name: "Slide Deck",
  };
  isCopySlideModalOpen.value = true;
};

const openCopyResourceModal = (material) => {
  currentCopyResource.value = {
    url: material.url,
    name: material.name,
  };
  isCopySlideModalOpen.value = true;
};

const loadActivityData = async () => {
  const activityId = route.params.id;

  if (!activityId) {
    error.value = "No lesson materials ID provided";
    isLoading.value = false;
    return;
  }

  try {
    isLoading.value = true;
    error.value = "";

    const response =
      await makercartActivityService.getEducatorGuideById(activityId);

    if (response.data && response.data.data) {
      const apiData = response.data.data;

      // Map API data to component data
      activityData.value.id = apiData.id;
      activityData.value.title = apiData.title;
      activityData.value.short_description = apiData.short_description;
      activityData.value.duration = apiData.duration;
      activityData.value.grade = apiData.grade;
      activityData.value.subject = apiData.subject;
      activityData.value.focus = apiData.focus;
      activityData.value.maker_approach = apiData.maker_approach;
      activityData.value.slide_url = apiData.slide_url;
      activityData.value.educator_guide_instructional =
        apiData.educator_guide_instructional || [];
      activityData.value.material_provided = apiData.material_provided || [];
      activityData.value.categories = apiData.categories || [];
      activityData.value.status = apiData.status;
      activityData.value.public_visibility = apiData.public_visibility;

      // Parse and set activity content
      const contents = JSON.parse(apiData.content || "[]");
      if (contents && contents.length > 0) {
        activityData.value.tabs = contents;
      }

      // Initialize DOM observer after content is loaded
      nextTick(() => {
        if (lessonTabContentRef.value) {
          runDomObserver();
        }
      });
    } else {
      throw new Error("Invalid response format");
    }
  } catch (err) {
    console.error("Error loading lesson materials:", err);
    error.value =
      err.response?.data?.message ||
      "Failed to load lesson materials. Please try again.";
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  // Check authentication
  const token = await cookiesService.getCookie("authToken");
  if (token) {
    isAuthenticated.value = true;
  }

  loadActivityData();
});

onBeforeUnmount(() => {
  if (domObserver) {
    domObserver.disconnect();
  }
  if (lessonTabContentRef.value) {
    const tooltipBlocks = lessonTabContentRef.value.querySelectorAll(
      ".tooltip-title-line"
    );
    tooltipBlocks.forEach((titleLine) => {
      if (titleLine.__accordionClickHandler__) {
        titleLine.removeEventListener(
          "click",
          titleLine.__accordionClickHandler__
        );
        delete titleLine.__accordionClickHandler__;
      }
    });
  }
});

const goBack = () => {
  router.push({
    name: "LessonMaterialsEditPage",
    params: { id: route.params.id },
  });
};

watch(activeTabIndex, () => {
  nextTick(() => {
    if (lessonTabContentRef.value) {
      initAccordions(lessonTabContentRef.value);
      setupSlideThumbnailHandlers(lessonTabContentRef.value);
    }
  });
});

// Watch for route changes
watch(
  () => route.params.id,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      activeTabIndex.value = 0;
      loadActivityData();
    }
  }
);
</script>

<style scoped>
.lesson-preview-container {
  margin: 0 auto;
  padding: 35px 0 35px 35px;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 40px);
}

.error-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.error-content {
  text-align: center;
  color: #666;
}

.error-content svg {
  color: #dc3545;
  margin-bottom: 20px;
}

.error-content h2 {
  margin: 0 0 10px 0;
  color: #333;
}

.error-content p {
  margin: 0 0 20px 0;
  color: #666;
}

.lesson-content-wrapper {
  display: flex;
  gap: 30px;
}

.lesson-main-content {
  flex: 1;
  min-width: 0;
}

.lesson-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 20px;
  margin-bottom: 20px;
}

.lesson-header-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
}

.lesson-navigation-container {
  display: flex;
  align-items: center;
}

.lesson-navigation-list {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  margin: 0;
}

.lesson-navigation-list li {
  font-size: 0.9rem;
  list-style: none;
}

.lesson-navigation-list li a {
  text-decoration: none;
  color: #333;
}

.lesson-navigation-title {
  color: var(--primary-pink);
  font-weight: 600;
}

.lesson-title-container {
  width: 100%;
}

.lesson-title {
  font-size: 2.5em;
  margin: 0 0 10px 0;
  color: #333;
}

.lesson-subtitle {
  font-size: 1.2em;
  color: #666;
  margin-bottom: 20px;
}

.lesson-meta {
  display: flex;
  gap: 20px;
  color: #777;
  align-items: center;
  margin-top: 20px;
  flex-wrap: wrap;
}

.lesson-meta-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--primary-blue);
}

.lesson-meta-item-icon {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lesson-main-container {
  display: flex;
  position: relative;
  flex: 1;
}

.lesson-content {
  flex: 1;
  transition: all 0.3s ease;
  border-radius: 8px;
  background-color: #fff;
  margin-right: 20px;
}

.lesson-content.expanded {
  margin-right: 50px;
}

.lesson-tabs {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  padding-bottom: 40px;
  border-bottom: 1px solid #e0e0e0;
  overflow-x: auto;
}

.lesson-tab {
  padding: 8px 20px;
  cursor: pointer;
  color: #777;
  position: relative;
  transition: all 0.2s ease;
  white-space: nowrap;
  border-bottom: 3px solid transparent;
}

.lesson-tab:hover {
  color: var(--primary-pink, #ff5a5f);
  border-bottom-color: var(--primary-pink, #ff5a5f);
}

.lesson-tab.active {
  border-bottom: 3px solid var(--primary-pink, #ff5a5f);
  color: var(--primary-pink, #ff5a5f);
  font-weight: bold;
}

.tab-duration {
  font-size: 0.8em;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -30px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.lesson-tab-content {
  min-height: 300px;
}

.lesson-section {
  margin-bottom: 30px;
}

/* Content styles are now handled globally in tiptap-editor.css */
.tiptap-content h2 {
  margin-top: 0;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.resources-section {
  margin: 40px 0;
}

.resources-section h2 {
  margin-bottom: 20px;
  color: #333;
}

.resources-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}

/* Sidebar Styles */
.activity-sidebar {
  width: 350px;
  min-width: 350px;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  height: fit-content;
  position: sticky;
  top: 20px;
  transition: all 0.3s ease;
}

.activity-sidebar.collapsed {
  width: 60px;
  min-width: 60px;
}

.sidebar-header {
  padding: 15px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
}

.sidebar-toggle {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.sidebar-toggle:hover {
  background-color: #f5f5f5;
  color: #333;
}

.sidebar-content {
  padding: 0;
}

.sidebar-section {
  border-bottom: 1px solid #f0f0f0;
  padding: 20px;
}

.sidebar-section:last-child {
  border-bottom: none;
}

.sidebar-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.1em;
  font-weight: 600;
  color: #333;
  margin: 0 0 15px 0;
}

.sidebar-section-title svg {
  color: var(--primary-blue, #3498db);
}

.materials-count {
  font-size: 0.8em;
  color: #666;
  font-weight: 400;
  margin-left: 5px;
}

.materials-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.material-item {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 12px;
  border-left: 4px solid var(--primary-blue, #3498db);
  transition: all 0.2s ease;
}

.material-item:hover {
  background-color: #f5f5f5;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.material-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
  gap: 10px;
}

.material-name {
  margin: 0;
  font-size: 0.95em;
  font-weight: 600;
  color: #333;
  line-height: 1.3;
  flex: 1;
}

.material-reference {
  margin: 0;
  color: #666;
  font-size: 0.8em;
}

.included-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: var(--green-dark-1);
  color: white;
  padding: 3px 6px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.maker-approach-content {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  border-left: 4px solid var(--primary-blue, #3498db);
}

.maker-approach-text {
  margin: 0;
  font-size: 0.95em;
  color: #555;
  line-height: 1.5;
  font-style: italic;
}

.maker-approach-text.tooltip-trigger {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  transition: all 0.2s ease;
  padding: 2px;
  border-radius: 4px;
}

.maker-approach-text.tooltip-trigger:hover {
  color: #333;
}

.info-icon {
  color: var(--primary-blue, #003d6a);
  opacity: 0.7;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.maker-approach-text.tooltip-trigger:hover .info-icon {
  opacity: 1;
  transform: scale(1.1);
}

.activity-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: #f9f9f9;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
}

.detail-label {
  font-weight: 500;
  color: #555;
  font-size: 0.9em;
}

.detail-value {
  font-weight: 600;
  color: #333;
  font-size: 0.9em;
  text-align: right;
}

.detail-value.status {
  padding: 3px 8px;
  border-radius: 12px;
  color: white;
  font-size: 0.8em;
  text-transform: capitalize;
}

.detail-value.status.published {
  background-color: #28a745;
}

.detail-value.status.draft {
  background-color: #6c757d;
}

.detail-value.status.archived {
  background-color: #dc3545;
}

.standards-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.standard-category {
  background: #ffffff;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #e8e8e8;
  transition: all 0.2s ease;
}

.standard-category:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.standard-category-title {
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  font-size: 0.95rem;
  text-transform: capitalize;
}

.standard-terms {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.standard-tag {
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.8rem;
  padding: 6px 12px;
  color: #374151;
  display: inline-block;
  transition: all 0.2s ease;
  cursor: default;
}

.standard-tag:hover {
  background-color: #e5e7eb;
  border-color: #d1d5db;
}

.empty-standards-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 15px 10px;
  text-align: center;
  background-color: #f8fafc;
  border: 1px dashed #e2e8f0;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.empty-standards-state:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
}

.empty-standards-icon {
  margin-bottom: 6px;
  opacity: 0.6;
  transition: all 0.3s ease;
}

.empty-standards-state:hover .empty-standards-icon {
  opacity: 0.8;
  transform: translateY(-1px);
}

.empty-standards-text {
  color: #94a3b8;
  font-size: 0.75rem;
  margin: 0;
  font-style: italic;
}

.action-buttons {
  display: flex;
  justify-content: center;
  margin-top: 30px;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background-color: #f0f0f0;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.2s;
  color: #333;
  font-weight: 500;
}

.back-button:hover {
  background-color: #e0e0e0;
}

@media (max-width: 1024px) {
  .lesson-content-wrapper {
    flex-direction: column;
    gap: 20px;
  }

  .activity-sidebar {
    width: 100%;
    min-width: auto;
    position: static;
    order: -1;
  }

  .activity-sidebar.collapsed {
    width: 100%;
    min-width: auto;
  }

  .sidebar-header {
    justify-content: space-between;
    align-items: center;
  }

  .sidebar-header::before {
    content: "Activity Information";
    font-weight: 600;
    color: #333;
  }
}

@media (max-width: 768px) {
  .lesson-preview-container {
    padding: 20px 15px;
  }

  .lesson-header-content {
    flex-direction: column;
  }

  .lesson-main-container {
    flex-direction: column-reverse;
  }

  .lesson-content {
    width: 100%;
    margin-right: 0;
  }

  .lesson-tabs {
    overflow-x: auto;
    white-space: nowrap;
    padding-bottom: 10px;
  }

  .lesson-tab {
    display: inline-block;
  }

  .resources-buttons {
    width: 100%;
  }

  .lesson-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .activity-sidebar {
    border-radius: 8px;
  }

  .sidebar-section {
    padding: 15px;
  }

  .material-item {
    padding: 10px;
  }

  .material-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .included-badge {
    align-self: flex-start;
  }

  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .detail-value {
    text-align: left;
  }

  /* Standards section mobile adjustments */
  .standards-section {
    padding: 15px;
  }

  .standard-category {
    padding: 10px;
  }

  .standard-category-title {
    font-size: 0.9rem;
    margin-bottom: 6px;
  }

  .standard-terms {
    gap: 6px;
  }

  .standard-tag {
    font-size: 0.75rem;
    padding: 5px 10px;
  }

  .empty-standards-state {
    padding: 12px 8px;
  }

  .empty-standards-text {
    font-size: 0.7rem;
  }
}
</style>

<!-- Global styles for rendered content -->
<style>
/* Slide deck styles */
.slide-deck-container {
  margin: 1.5rem 0;
  border-radius: 8px;
  background-color: var(--white-hover-1);
}

.slide-deck-content {
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding: 8px;
  align-items: flex-start;
}

.slide-deck-thumbnail {
  flex-shrink: 0;
  width: 469px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.slide-deck-thumbnail:hover {
  opacity: 0.9;
}

.slide-deck-thumbnail img {
  width: 100%;
  height: 265px;
  object-fit: cover;
  border-radius: 0.75rem;
  transition: transform 0.2s ease;
}

.slide-deck-thumbnail:hover img {
  transform: scale(1.03);
}

.slide-deck-title {
  font-weight: 500;
  color: var(--black-body-text);
  margin-top: 0.5rem;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.08px;
}

.slide-deck-info {
  flex: 1 0 0;
  min-width: 1px;
  padding-left: 16px;
  border-left: 2px solid var(--neutral-100);
  align-self: center;
}

.slide-deck-notes {
  color: var(--black-body-text);
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.15px;
  font-family: "League Spartan", sans-serif;
  min-height: 50px;
}

@media (max-width: 768px) {
  .slide-deck-content {
    flex-direction: column;
  }

  .slide-deck-thumbnail {
    width: 100%;
  }

  .slide-deck-thumbnail img {
    height: auto;
    aspect-ratio: 469 / 265;
  }

  .slide-deck-info {
    border-left: none;
    padding-left: 0;
  }

  .slide-deck-notes {
    font-size: var(--fs-small);
    line-height: 20px;
    text-align: left;
  }
}
</style>
